import React, { useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  EVAvatar,
  EVBox,
  EVTypography,
  EVKeyboardArrowDown,
} from '@eagleview/ev-comp-library';
import UserMenu from './components/UserMenu.component';
import UserProfileErrorAvatar from './UserProfileErrorAvatar.component';
import useStyles from './UserProfileStyle';

/**
 * Component that shows a monogram and the name of the user
 */
const UserProfile = (props) => {
  const {
    monogramVariant, userDetails, withUserMenu, setViewProfile, userProfileError, isGuestUser,
  } = props;
  const classes = useStyles();
  const { firstName, lastName } = userDetails;
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const initials = (firstName ? firstName.charAt(0).toUpperCase() : '') + (lastName ? lastName.charAt(0).toUpperCase() : '');
  const avatarRef = React.useRef();

  /**
   * Opens the context menu
   */
  const handleClick = () => {
    if (userProfileError) return; // Don't open menu if there's an error
    setAnchorEl(avatarRef.current);
    setOpen(true);
  };

  /**
   * Closes the context menu
   */
  const handleMenuClickAway = () => {
    setOpen(false);
  };

  // If there's a user profile error, show a dummy non-clickable avatar
  if (userProfileError) {
    return <UserProfileErrorAvatar monogramVariant={monogramVariant} />;
  }

  // Guest user: first letter of email/username in avatar, email below (from user profile v2), menu with only Logout → /signin?redirect. #genai
  if (isGuestUser && withUserMenu) {
    const rawEmail = userDetails.email || userDetails.userName || '';
    const guestInitial = rawEmail.charAt(0).toUpperCase() || '--';
    const displayEmail = rawEmail ? rawEmail.charAt(0).toUpperCase() + rawEmail.slice(1).toLowerCase() : '--';
    return (
      <div className={classes.container}>
        <EVBox className={classes.box}>
          <EVAvatar
            id="userAvatar"
            ref={avatarRef}
            className={clsx({ [classes.avatarMicro]: monogramVariant === 'micro' },
              { [classes.avatarSmall]: monogramVariant === 'small' },
              { [classes.avatarMedium]: monogramVariant === 'medium' },
              { [classes.avatarLarge]: monogramVariant === 'large' })}
            onClick={handleClick}
          >
            {guestInitial}
          </EVAvatar>
          { monogramVariant === 'medium' && (
            <EVKeyboardArrowDown id="keyboardArrowDown" className={classes.keyboardArrowDown} onClick={handleClick} />
          )}
        </EVBox>
        { monogramVariant === 'medium' && (
          <EVTypography id="fullName" className={classes.guestName}>
            {displayEmail}
          </EVTypography>
        )}
        <UserMenu
          setViewProfile={setViewProfile}
          handleMenuClickAway={handleMenuClickAway}
          anchorEl={anchorEl}
          open={open}
          monogramVariant={monogramVariant}
          userDetails={userDetails}
          isGuestUser
        />
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <EVBox className={classes.box}>
        <EVAvatar
          id="userAvatar"
          ref={avatarRef}
          className={clsx({ [classes.avatarMicro]: monogramVariant === 'micro' },
            { [classes.avatarSmall]: monogramVariant === 'small' },
            { [classes.avatarMedium]: monogramVariant === 'medium' },
            { [classes.avatarLarge]: monogramVariant === 'large' })}
          onClick={handleClick}
        >
          {initials}
        </EVAvatar>
        { monogramVariant === 'medium' && (
          <EVKeyboardArrowDown id="keyboardArrowDown" className={classes.keyboardArrowDown} onClick={handleClick} />
        )}
      </EVBox>
      { monogramVariant === 'medium' && <EVTypography id="fullName" className={classes.name}>{`${firstName} ${lastName}`}</EVTypography>}
      { withUserMenu
        && (
        <UserMenu
          setViewProfile={setViewProfile}
          handleMenuClickAway={handleMenuClickAway}
          anchorEl={anchorEl}
          open={open}
          monogramVariant={monogramVariant}
          userDetails={userDetails}
          isGuestUser={false}
        />
        )}
    </div>
  );
};

UserProfile.defaultProps = {
  monogramVariant: 'small',
  withUserMenu: false,
  setViewProfile: null,
  userProfileError: false,
  isGuestUser: false,
};

UserProfile.propTypes = {
  /**
   * Variant of monogram
   * @property {string} monogramVariant
   */
  monogramVariant: PropTypes.oneOf(['micro', 'small', 'medium', 'large']),

  /**
   * Details of the logged in user
   * @property {Object} userDetails
   */
  userDetails: PropTypes.shape().isRequired,

  /**
   * Indicates whether the component should have user menu or not
   * @property {bool} withUserMenu
   */
  withUserMenu: PropTypes.bool,

  /**
   * function to show the view profile panel
   * @property {function} setViewProfile
   */
  setViewProfile: PropTypes.func,

  /**
   * Indicates whether there's a user profile error
   * @property {bool} userProfileError
   */
  userProfileError: PropTypes.bool,

  /**
   * Guest user: show "--" avatar, menu with only Logout → /signin?redirect. #genai
   * @property {bool} isGuestUser
   */
  isGuestUser: PropTypes.bool,
};

export default UserProfile;
